/**
 * SearchPalette — global ⌘K / Ctrl+K command palette, opened from the Topbar
 * search input.
 *
 * Searches two sources client-side:
 *  - Pages: static list of in-app destinations (label resolved via i18n, so
 *    matching works in both EN and FR).
 *  - Topics: fetched once via the existing `/api/topics` endpoint (react-query,
 *    scoped to the active certification). Selecting a topic creates a study
 *    session filtered on it and routes into `/study/session/:id` — same
 *    create-then-navigate flow as the Study Mix tiles (useLaunchSession).
 *
 * Keyboard: ↑/↓ move, Enter selects, Esc closes. The list is capped at 8 rows
 * (pages first), results re-rank as the query narrows.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchTopics } from '../../api/questions';
import { useCertStore } from '../../stores/certStore';
import { useLaunchSession } from '../study/mix/useLaunchSession';

const MAX_RESULTS = 8;

interface PageEntry {
  labelKey: string;
  to: string;
}

/** In-app destinations searchable by their localized label. */
const PAGES: PageEntry[] = [
  { labelKey: 'nav.dashboard', to: '/dashboard' },
  { labelKey: 'nav.studyMode', to: '/study' },
  { labelKey: 'nav.mockExams', to: '/exam' },
  { labelKey: 'nav.review', to: '/review' },
  { labelKey: 'nav.progress', to: '/profile' },
  { labelKey: 'nav.billing', to: '/billing' },
  { labelKey: 'nav.pricing', to: '/pricing' },
];

type Result =
  | { kind: 'page'; label: string; to: string }
  | { kind: 'topic'; label: string; topic: string; count: number };

/** Lowercase + strip accents so "reseau" matches "Réseaux virtuels". */
function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export interface SearchPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function SearchPalette({ open, onClose }: SearchPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const certCode = useCertStore((s) => s.certCode);
  const { launch, isPending } = useLaunchSession();

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const { data: topics } = useQuery({
    queryKey: ['topics', certCode],
    queryFn: () => fetchTopics(certCode ?? undefined),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  /* Focus the input each time the palette opens (state is reset on close). */
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  /* Close + reset so the next open starts from a blank query. */
  const close = useCallback(() => {
    setQuery('');
    setActiveIndex(0);
    onClose();
  }, [onClose]);

  const results = useMemo<Result[]>(() => {
    const q = normalize(query.trim());
    const pages: Result[] = PAGES.map((p) => ({ kind: 'page' as const, label: t(p.labelKey), to: p.to }));
    const topicEntries: Result[] = (topics ?? []).map((tp) => ({
      kind: 'topic' as const,
      label: tp.topic,
      topic: tp.topic,
      count: tp.count,
    }));
    const all = [...pages, ...topicEntries];
    if (!q) return all.slice(0, MAX_RESULTS);
    return all
      .map((r) => {
        const label = normalize(r.label);
        const idx = label.indexOf(q);
        return { r, rank: idx === 0 ? 0 : idx > 0 ? 1 : 2 };
      })
      .filter((x) => x.rank < 2)
      .sort((a, b) => a.rank - b.rank)
      .map((x) => x.r)
      .slice(0, MAX_RESULTS);
  }, [query, topics, t]);

  /* Clamp instead of resetting in an effect — the raw index may exceed the
     result count after the query narrows. */
  const active = Math.max(0, Math.min(activeIndex, results.length - 1));

  const select = useCallback(
    (r: Result) => {
      if (r.kind === 'page') {
        close();
        navigate(r.to);
      } else {
        // Close immediately — useLaunchSession navigates once the session is created.
        launch({ name: r.label, topicFilter: r.topic });
        close();
      }
    },
    [launch, navigate, close],
  );

  /* Keyboard navigation inside the palette. */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(Math.min(active + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(Math.max(active - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const r = results[active];
      if (r) select(r);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  /* Keep the active row visible while arrowing through the list. */
  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const renderGroup = (kind: Result['kind'], heading: string) => {
    const group = results.filter((r) => r.kind === kind);
    if (group.length === 0) return null;
    return (
      <li>
        <div className="px-3 pb-1 pt-3 text-[10px] font-extrabold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
          {heading}
        </div>
        <ul>
          {group.map((r) => {
            const index = results.indexOf(r);
            const isActive = index === active;
            return (
              <li key={r.kind === 'page' ? r.to : r.topic} data-index={index}>
                <button
                  type="button"
                  onClick={() => select(r)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300'
                      : 'text-slate-700 dark:text-slate-200'
                  }`}
                >
                  <span className="text-slate-400 dark:text-slate-500" aria-hidden="true">
                    {r.kind === 'page' ? (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M13 6l6 6-6 6" />
                      </svg>
                    ) : (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 4h7a3 3 0 0 1 3 3v13a2 2 0 0 0-2-2H2V4ZM22 4h-7a3 3 0 0 0-3 3v13a2 2 0 0 1 2-2h8V4Z" />
                      </svg>
                    )}
                  </span>
                  <span className="flex-1 truncate">{r.label}</span>
                  {r.kind === 'topic' && (
                    <span className="font-mono text-[11px] text-slate-400 tabular-nums dark:text-slate-500" style={{ fontFeatureSettings: '"tnum"' }}>
                      {t('nav.searchQuestionCount', { count: r.count })}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </li>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 px-4 pt-[12vh] backdrop-blur-[2px]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={t('nav.searchPlaceholder')}
    >
      <div className="w-full max-w-[560px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-canvas-dark-2">
        {/* Input row */}
        <div className="relative border-b border-slate-100 dark:border-white/10">
          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" aria-hidden="true">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
          </span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
            onKeyDown={handleKeyDown}
            placeholder={t('nav.searchPlaceholder')}
            disabled={isPending}
            className="h-[52px] w-full bg-transparent pl-11 pr-4 text-[15px] text-slate-800 placeholder:text-slate-400 outline-none dark:text-slate-100 dark:placeholder:text-slate-500"
            role="combobox"
            aria-expanded="true"
            aria-controls="search-palette-results"
            aria-activedescendant={results[active] ? `search-result-${active}` : undefined}
          />
        </div>

        {/* Results */}
        <ul id="search-palette-results" ref={listRef} className="max-h-[320px] overflow-y-auto pb-2" role="listbox">
          {results.length === 0 ? (
            <li className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
              {t('nav.searchNoResults', { query })}
            </li>
          ) : (
            <>
              {renderGroup('page', t('nav.searchGroupPages'))}
              {renderGroup('topic', t('nav.searchGroupTopics'))}
            </>
          )}
        </ul>

        {/* Footer hint */}
        <div className="flex items-center gap-3 border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400 dark:border-white/10 dark:text-slate-500">
          <span><kbd className="font-semibold">↑↓</kbd> {t('nav.searchHintNavigate')}</span>
          <span><kbd className="font-semibold">↵</kbd> {t('nav.searchHintSelect')}</span>
          <span><kbd className="font-semibold">esc</kbd> {t('nav.searchHintClose')}</span>
        </div>
      </div>
    </div>
  );
}
